/**
 * This file contains many functions that make calls to our API and return the promises from those calls.
 */
import { flatMap, range } from 'lodash';
import { parallelLimit } from 'async';
import queryString from 'querystring';

import Api from '../api.ts';

const PURCHASE_ORDER = '/purchase-orders';

const PurchaseOrderAPI = {
  /**
   * Gets on po by po number
   *
   * @param poNumber
   * @returns {Promise<purchaseOrder>}
   */
  async findOneByPONumber(poNumber, params) {
    return Api.get(
      `${PURCHASE_ORDER}/${poNumber}?${queryString.encode(params)}`,
    );
  },

  /**
   * Get open purchase orders
   *
   * @returns {promise} promise returned from api call that eventually
   * resolves to an array of purchase orders.
   *
   * example: purchaseOrders: [purchaseOrder1, purchaseOrder2, ...]
   */
  async getOpenOrders() {
    const limit = 25;
    const firstPage = await Api.get(
      `${PURCHASE_ORDER}/open-orders?${queryString.encode({
        page: 1,
      })}`,
    );

    const { count } = firstPage;
    const totalPages = Math.ceil(count / limit);

    const purchaseOrders = await parallelLimit(
      range(totalPages).map((page) => {
        const params = queryString.encode({ page: page + 1, limit });
        return (cb) =>
          Api.get(`${PURCHASE_ORDER}/open-orders?${params}`)
            .then((orders) => cb(null, orders))
            .catch(cb);
      }),
      5,
    ).catch(console.log);

    return flatMap(purchaseOrders, 'purchaseOrders');
  },

  /**
   * Get pending purchase orders
   *
   * @returns {promise} promise returned from api call that eventually
   * resolves to an array of purchase orders.
   *
   * example: purchaseOrders: [purchaseOrder1, purchaseOrder2, ...]
   */
  async getPendingOrders() {
    const { purchaseOrders } = await Api.get(
      `${PURCHASE_ORDER}/pending-orders`,
    );
    return purchaseOrders;
  },

  /**
   * Get scheduled purchase orders
   *
   * @returns {promise} promise returned from api call that eventually
   * resolves to an array of purchase orders.
   *
   * example: purchaseOrders: [purchaseOrder1, purchaseOrder2, ...]
   */
  async getScheduledOrders() {
    const limit = 25;
    const firstPage = await Api.get(
      `${PURCHASE_ORDER}/scheduled-orders?${queryString.encode({
        page: 1,
      })}`,
    );

    const { count } = firstPage;
    const totalPages = Math.ceil(count / limit);

    const purchaseOrders = await parallelLimit(
      range(totalPages).map((page) => {
        const params = queryString.encode({ page: page + 1, limit });
        return (cb) =>
          Api.get(`${PURCHASE_ORDER}/scheduled-orders?${params}`)
            .then((orders) => cb(null, orders))
            .catch(cb);
      }),
      5,
    ).catch(console.log);

    return flatMap(purchaseOrders, 'purchaseOrders');
  },
};

export default PurchaseOrderAPI;
